import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, request } from "../api/client";
import type { FacilityCommand, Page } from "./types";

export function facilityRead<T>(view: string, query: Record<string, string> = {}, signal?: AbortSignal) {
  const params = new URLSearchParams(Object.entries(query).filter(([, value]) => value !== ""));
  return request<T>(`/facilities/${view}${params.size ? "?" + params : ""}`, "GET", undefined, signal);
}
export function useFacilityPage<T>(view: string, query: Record<string, string>, revision: number, enabled = true) {
  const [items, setItems] = useState<T[]>([]), [cursor, setCursor] = useState<string | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState<unknown>();
  const abort = useRef<AbortController | null>(null), encoded = JSON.stringify(query);
  const load = useCallback(async (next = "") => {
    if (!enabled) return;
    abort.current?.abort(); const controller = new AbortController(); abort.current = controller;
    setBusy(true); setError(undefined); if (!next) { setItems([]); setCursor(null); }
    try {
      const page = await facilityRead<Page<T>>(view, { ...JSON.parse(encoded), cursor: next }, controller.signal);
      if (!Array.isArray(page.items)) throw new Error("Invalid facility list");
      if (!controller.signal.aborted) { setItems(old => next ? [...old, ...page.items] : page.items); setCursor(page.nextCursor || null); }
    } catch (failure) { if (!controller.signal.aborted) { setError(failure); setItems([]); setCursor(null); } }
    finally { if (!controller.signal.aborted) setBusy(false); }
  }, [view, encoded, revision, enabled]);
  useEffect(() => { void load(); return () => abort.current?.abort(); }, [load]);
  return { items, cursor, busy, error, load };
}
export function useFacilityCommand(organizationId: string, saved: () => void): FacilityCommand {
  const [busy, setBusy] = useState(false), [uncertain, setUncertain] = useState(false), [error, setError] = useState<unknown>(), [result, setResult] = useState<Record<string, unknown>>();
  const running = useRef(false), attempt = useRef<Record<string, unknown> | null>(null);
  async function send(input?: Record<string, unknown>) {
    if (running.current) return false;
    if (!attempt.current && input) attempt.current = { ...input, organizationId, requestId: crypto.randomUUID() };
    if (!attempt.current) return false;
    running.current = true; setBusy(true); setError(undefined); setResult(undefined);
    try {
      const value = await request<Record<string, unknown>>("/facilities/command", "POST", attempt.current);
      if (value.organizationId !== organizationId) throw new Error("Invalid facility response scope");
      attempt.current = null; setUncertain(false); setResult(value); saved(); return true;
    } catch (failure) {
      const unknown = !(failure instanceof ApiError) || failure.status >= 500 || failure.status === 408;
      if (!unknown) attempt.current = null;
      setUncertain(unknown); setError(failure); return false;
    } finally { running.current = false; setBusy(false); }
  }
  useEffect(() => {
    if (!busy && !uncertain) return;
    const prevent = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", prevent); return () => window.removeEventListener("beforeunload", prevent);
  }, [busy, uncertain]);
  function clearFeedback() {
    // Never discard the outcome or identity of an in-flight/uncertain write.
    if (running.current || attempt.current) return;
    setError(undefined);
  }
  return { send, clearFeedback, busy, uncertain, error, result, locked: busy || uncertain };
}
